import {
  Alert,
  Box,
  Button,
  Drawer,
  Group,
  Loader,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Skeleton,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
  Tooltip as MantineTooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { Navigate, useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { hasRole, useCurrentUser } from "../hooks/useAuth.js";
import { useEmployees } from "../hooks/useEmployees.js";
import {
  computeGrowthDomain,
  normalizeForSearch,
  pinGrowth,
  type GrowthDomain,
} from "../lib/bcgAxis.js";
import { toLocalDateString } from "../lib/date.js";
import { useSegments } from "../hooks/useSegments.js";
import {
  BCG_QUADRANT_COLORS,
  CHART_COLORS,
  LEAD_PALETTE,
} from "../theme.js";
import {
  useBcgMatrix,
  useBcgThresholds,
  useUpdateThreshold,
  type BcgCustomer,
} from "../hooks/useBcg.js";
import type { PeriodInput } from "@msc/shared";

// ── Search-Param Schema ───────────────────────────────────────────────────────

export interface BcgSearch {
  segmentId?: string;
  periodKind?: "rolling_12m" | "fiscal_year" | "custom";
  fiscalYear?: string;
  periodStart?: string;
  periodEnd?: string;
  includeInactive?: boolean;
  /** Blase pro (Kunde × Segment)-Zelle oder pro Beauftragung (S2-11). */
  granularity?: "customer" | "engagement";
  /**
   * Anzeigefilter „nur meine Kunden" (Befund G1). `undefined` = noch nicht
   * gesetzt → es gilt die Rollen-Vorbelegung (Projektleiter: an, alle
   * anderen: aus). Erst das Umschalten schreibt einen festen Wert in die URL,
   * damit ein geteilter Link zeigt, was der Absender gesehen hat.
   */
  onlyMine?: boolean;
}

export function validateBcgSearch(raw: Record<string, unknown>): BcgSearch {
  const periodKind = (raw.periodKind as string | undefined) ?? "rolling_12m";
  return {
    onlyMine:
      raw.onlyMine === undefined
        ? undefined
        : raw.onlyMine === true || raw.onlyMine === "true",
    segmentId: typeof raw.segmentId === "string" ? raw.segmentId : undefined,
    periodKind:
      periodKind === "rolling_12m" || periodKind === "fiscal_year" || periodKind === "custom"
        ? periodKind
        : "rolling_12m",
    fiscalYear: typeof raw.fiscalYear === "string" ? raw.fiscalYear : undefined,
    periodStart: typeof raw.periodStart === "string" ? raw.periodStart : undefined,
    periodEnd: typeof raw.periodEnd === "string" ? raw.periodEnd : undefined,
    includeInactive: raw.includeInactive === true || raw.includeInactive === "true",
    granularity: raw.granularity === "engagement" ? "engagement" : "customer",
  };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function buildPeriodInput(search: BcgSearch): PeriodInput {
  switch (search.periodKind) {
    case "fiscal_year":
      return {
        kind: "fiscal_year",
        fiscalYearLabel: search.fiscalYear ?? String(new Date().getFullYear()),
      };
    case "custom":
      return {
        kind: "custom",
        start: search.periodStart ?? formatIsoDate(new Date(Date.now() - 365 * 86400000)),
        end: search.periodEnd ?? formatIsoDate(new Date()),
      };
    default:
      return { kind: "rolling_12m" };
  }
}

// Dünner Wrapper um `toLocalDateString` (Befund B1) — hält die vorhandenen
// Aufrufstellen in dieser Datei unverändert, statt sie einzeln umzubenennen.
function formatIsoDate(d: Date): string {
  return toLocalDateString(d);
}

function formatRevenue(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} M€`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(0)} k€`;
  return `${v.toFixed(0)} €`;
}

function formatGrowth(v: number | null): string {
  if (v === null) return "–";
  return `${(v * 100).toFixed(1)} %`;
}

/** Stable color by index — same projectLeadId always gets same color.
 *  Palette + Quadranten-Farben kommen zentral aus dem mindsquare-Theme. */
function getLeadColor(leadId: string | null, colorMap: Map<string, string>): string {
  if (!leadId) return CHART_COLORS.neutral;
  const cached = colorMap.get(leadId);
  if (cached !== undefined) return cached;
  const color =
    LEAD_PALETTE[colorMap.size % LEAD_PALETTE.length] ?? CHART_COLORS.neutral;
  colorMap.set(leadId, color);
  return color;
}

function getQuadrantColor(quadrant: string): string {
  return BCG_QUADRANT_COLORS[quadrant] ?? CHART_COLORS.neutral;
}

/** Scale bubble size: sqrt of revenue, capped between 10 and 60 */
function bubbleSize(revenue: number, minRev: number, maxRev: number): number {
  if (maxRev <= minRev) return 30;
  const normalized = (revenue - minRev) / (maxRev - minRev);
  return Math.max(10, Math.min(60, 10 + Math.sqrt(normalized) * 50));
}

// ── Custom Tooltip ────────────────────────────────────────────────────────────

interface BcgTooltipPayload {
  id: string;
  name: string;
  revenue: number;
  growth: number | null;
  quadrant: string;
  lastOnsiteVisitDate: string | null;
  x: number;
  y: number;
  z: number;
  fill: string;
  marker: "new" | "inactive" | null;
  /** Nur granularity=engagement gesetzt. */
  customerId?: string;
  engagementName?: string;
  leadName?: string | null;
  /** Der Angemeldete ist Service Lead dieser Blase (G1). */
  isMyCustomer: boolean;
  /** Die Kundenakte ist für ihn erreichbar — sonst ist die Blase kein Link (G2). */
  openable: boolean;
  /** Am Achsenrand gepinnter Ausreißer (G3); `y` ist dann nicht der Wert. */
  pinned: "low" | "high" | null;
  /** Treffer der Kundensuche — hervorgehoben statt gedimmt. */
  highlighted: boolean;
  dimmed: boolean;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: BcgTooltipPayload }>;
}

function BcgTooltip({ active, payload }: CustomTooltipProps) {
  const { t } = useTranslation();
  if (!active || !payload || payload.length === 0) return null;
  const entry = payload.at(0);
  if (!entry) return null;
  const d = entry.payload;
  const quadrantKey = d.quadrant as "star" | "cash_cow" | "question_mark" | "dog";
  const quadrantLabel =
    quadrantKey === "star" ||
    quadrantKey === "cash_cow" ||
    quadrantKey === "question_mark" ||
    quadrantKey === "dog"
      ? t(`quadrant.${quadrantKey}`)
      : d.quadrant;
  return (
    <Paper p="sm" shadow="md" withBorder>
      <Stack gap={4}>
        <Text fw={700} size="sm">
          {d.name}
        </Text>
        {d.engagementName && (
          <Text size="xs">
            {t("pages.bcg.tooltipEngagementLabel")}: <strong>{d.engagementName}</strong>
          </Text>
        )}
        {d.engagementName && (
          <Text size="xs">
            {t("pages.bcg.tooltipLeadNameLabel")}:{" "}
            <strong>{d.leadName ?? t("common.dashed")}</strong>
          </Text>
        )}
        <Text size="xs">
          {t("pages.bcg.tooltipRevenueLabel")}: <strong>{formatRevenue(d.revenue)}</strong>
        </Text>
        <Text size="xs">
          {t("pages.bcg.tooltipGrowthLabel")}: <strong>{formatGrowth(d.growth)}</strong>
        </Text>
        <Text size="xs">
          {t("pages.bcg.tooltipQuadrantLabel")}: <strong>{quadrantLabel}</strong>
        </Text>
        {d.marker && (
          <Text size="xs" c="dimmed">
            {t("pages.bcg.tooltipMarker")}:{" "}
            {d.marker === "new"
              ? t("pages.bcg.tooltipMarkerNew")
              : t("pages.bcg.tooltipMarkerInactive")}
          </Text>
        )}
        <Text size="xs" c="dimmed">
          {t("pages.bcg.tooltipLastVisit")}:{" "}
          {d.lastOnsiteVisitDate
            ? new Date(d.lastOnsiteVisitDate).toLocaleDateString("de-DE")
            : t("common.dashed")}
        </Text>
        {d.pinned && (
          <Text size="xs" c="dimmed">
            {t("pages.bcg.tooltipPinnedHint")}
          </Text>
        )}
        {d.openable ? (
          <Text size="xs" c="blue" style={{ cursor: "pointer" }}>
            {t("pages.bcg.tooltipClickHint")}
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            {t("pages.bcg.tooltipNotOpenableHint")}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

// ── Custom Dot with special markers ──────────────────────────────────────────

interface BcgDotProps {
  cx?: number;
  cy?: number;
  payload?: BcgTooltipPayload;
  r?: number;
  fill?: string;
  onClick?: (data: BcgTooltipPayload) => void;
}

function BcgDot({ cx = 0, cy = 0, payload, r = 10, fill = CHART_COLORS.ist, onClick }: BcgDotProps) {
  if (!payload) return null;

  const isNew = payload.marker === "new";
  const isInactive = payload.marker === "inactive";
  const { openable, pinned, highlighted, dimmed } = payload;

  // Suche (G3): Treffer bleiben voll deckend und bekommen einen Ring, alles
  // andere tritt zurück. Die Überlagerung im Zentrum bleibt geometrisch
  // bestehen — die Suche ist der Weg, trotzdem an eine Blase heranzukommen.
  const opacity = dimmed ? 0.15 : 1;

  return (
    <g
      data-testid="bcg-bubble"
      data-name={payload.name}
      data-openable={openable ? "true" : "false"}
      data-pinned={pinned ?? ""}
      data-highlighted={highlighted ? "true" : "false"}
      opacity={opacity}
    >
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={fill}
        // Deckkraft statt angehängtem Alpha: `fill` kann eine CSS-Variable sein (#337).
        fillOpacity={isInactive ? 0.53 : undefined}
        // Gepinnte Ausreißer bekommen eine kräftige Kontur — sie stehen nicht
        // an ihrem echten Platz und dürfen nicht so aussehen, als täten sie es.
        stroke={pinned ? "var(--msc-diagramm-text)" : isNew ? "var(--msc-diagramm-kontur)" : isInactive ? fill : "none"}
        strokeWidth={pinned ? 3 : isNew ? 2 : isInactive ? 3 : 0}
        strokeDasharray={isNew && !pinned ? "4 3" : undefined}
        // Nicht erreichbare Kundenakten sind kein Link — der Zeiger darf
        // nichts anderes behaupten (Befund G2).
        style={{ cursor: openable ? "pointer" : "default" }}
        onClick={openable ? () => onClick?.(payload) : undefined}
      />
      {highlighted && (
        <circle
          cx={cx}
          cy={cy}
          r={r + 6}
          fill="none"
          stroke="var(--msc-navy)"
          strokeWidth={3}
          pointerEvents="none"
        />
      )}
      {pinned && (
        /* Der ECHTE Wert steht an der Blase — bewusst zur Chart-Mitte hin
           (bei einem oberen Ausreißer also unterhalb): Die Blase klebt am
           Rand, außerhalb wäre die Beschriftung angeschnitten. Zusätzlich
           nennt die Zeile unter dem Chart Name und Wert. */
        <text
          x={cx}
          y={pinned === "high" ? cy + r + 14 : cy - r - 8}
          fontSize={11}
          fontWeight={600}
          textAnchor="middle"
          fill="var(--msc-diagramm-text)"
          pointerEvents="none"
        >
          {formatGrowth(payload.growth)}
        </text>
      )}
    </g>
  );
}

// ── Fiscal Year dropdown ──────────────────────────────────────────────────────

const FISCAL_YEARS = Array.from({ length: 5 }, (_, i) => {
  const y = new Date().getFullYear() - 2 + i;
  return { value: String(y), label: String(y) };
});

/** Höchstens so viele Suchtreffer als Zeile — darüber wird nur gezählt. */
const SEARCH_RESULT_LIMIT = 8;

// ── BCG Page ──────────────────────────────────────────────────────────────────

export function BcgPage() {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as BcgSearch;

  const periodKind = search.periodKind ?? "rolling_12m";
  const period = buildPeriodInput(search);
  const includeInactive = search.includeInactive ?? false;
  const granularity = search.granularity ?? "customer";

  const isBereichsleiter = hasRole(user, [
    "bereichsleiter",
    "stellvertreter",
    "admin",
  ]);

  const isGL = hasRole(user, "geschaeftsleitung");

  // Route-Guard symmetrisch zum AppLayout/Backend: BCG sieht nur, wer eine
  // fachlich relevante Rolle hat. Random-User ohne Rolle landen wieder
  // auf dem Dashboard. Den Check führen wir erst NACH allen Hooks aus,
  // damit wir nicht gegen die Rules-of-Hooks verstoßen.
  const canSeeBcg = hasRole(user, [
    "admin",
    "geschaeftsleitung",
    "bereichsleiter",
    "stellvertreter",
    "projektleiter",
  ]);

  // Threshold drawer
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  // Log-scale toggle (local state only — not worth URL persistence)
  const [logScale, setLogScale] = useState(false);
  // Wachstumsachse: begrenzt (Voreinstellung, Befund G3) oder volle Spanne.
  const [fullGrowthRange, setFullGrowthRange] = useState(false);
  // Kundensuche mit Hervorhebung (Befund G3) — der Ausweg aus der
  // Überlagerung, ohne eine Blase treffen zu müssen.
  const [customerQuery, setCustomerQuery] = useState("");
  // Bubble-Färbung: nach Quadrant (klassische BCG-Optik, sofort farbig) oder
  // nach zuständigem PL (grau, solange keiner zugewiesen ist).
  const [colorMode, setColorMode] = useState<"quadrant" | "lead">("quadrant");

  // Threshold override form state
  const [revenueOverride, setRevenueOverride] = useState<number | string>("");
  const [growthOverride, setGrowthOverride] = useState<number | string>("");

  // Segment options aus dem API-Endpoint (GET /segments).
  // Bereichsleiter sieht (Default) sein Segment, kann aber wechseln; Admin/GL können frei wählen.
  const { data: allSegments } = useSegments();
  const segmentOptions = (allSegments ?? []).map((s) => ({
    value: s.id,
    label: s.name,
  }));

  // Auflösung des Segments: explizit aus der URL → Standardsegment des Nutzers →
  // erstes geladenes Segment.
  //
  // **Die letzte Stufe gilt nur, wer auch wechseln darf.** `/segments` liefert
  // ALLE Segmente, der Wähler unten steht aber nur Bereichsleitern und der
  // Geschäftsleitung zur Verfügung. Für alle anderen war „erstes Segment
  // überhaupt" ein Ratespiel: Auf Produktion griff es bei jedem Projektleiter,
  // die Antwort war ein 403, und die Seite meldete „BCG-Daten konnten nicht
  // geladen werden" — ein Rechtefehler, der wie ein Defekt aussah. Wer nicht
  // wechseln kann, bekommt lieber gar kein Segment und einen klaren Satz
  // (2026-08-12).
  const darfSegmentWaehlen = isBereichsleiter || isGL;
  const resolvedSegmentId =
    search.segmentId ??
    user?.defaultSegmentId ??
    (darfSegmentWaehlen ? allSegments?.[0]?.id : undefined) ??
    undefined;

  // BCG Data
  const {
    data: matrixData,
    isLoading,
    isError,
  } = useBcgMatrix({
    segmentId: resolvedSegmentId ?? "",
    period,
    includeInactive,
    granularity,
  });

  const { data: thresholdsData } = useBcgThresholds({
    segmentId: resolvedSegmentId ?? "",
    period,
  });

  const updateThreshold = useUpdateThreshold();

  // ── Anzeigefilter „nur meine Kunden" (Befund G1) ─────────────────────────
  //
  // An den Rechten ändert sich nichts: Ein Projektleiter, der in einem Bereich
  // mindestens einen Kunden führt, darf den ganzen Bereich sehen — der
  // Vergleich mit dem Bereich ist der Zweck der Matrix. Was fehlte, war die
  // Möglichkeit, sich auf die eigenen Kunden zu beschränken. „Meine Kunden"
  // ist dabei dieselbe Ableitung wie überall sonst (customer_segment_leads);
  // die Wahrheit dazu kommt fertig aus der API (`isMyCustomer`), damit hier
  // keine zweite Regel entsteht.
  const isPrivilegedViewer = hasRole(user, [
    "admin",
    "geschaeftsleitung",
    "bereichsleiter",
    "stellvertreter",
  ]);
  const onlyMineDefault = !isPrivilegedViewer && hasRole(user, "projektleiter");
  const onlyMine = search.onlyMine ?? onlyMineDefault;

  // Derived data for chart
  const allBubbles = matrixData?.customers ?? [];
  const customers = onlyMine
    ? allBubbles.filter((c) => c.isMyCustomer)
    : allBubbles;
  const hiddenByOnlyMine = allBubbles.length - customers.length;
  // Wer ohnehin jede Kundenakte des Bereichs öffnen darf, sagt die API —
  // die Blasenklickbarkeit erfindet die Regel nicht neu (Befund G2).
  const viewerCanOpenAll = matrixData?.viewer.canOpenAllCustomers ?? false;
  const thresholds = matrixData?.thresholds;

  // Build color map (stable per render)
  const colorMap = new Map<string, string>();
  customers.forEach((c) => getLeadColor(c.projectLeadId, colorMap));

  // Legende für den Lead-Farbmodus: ein Eintrag je Lead in colorMap-Reihen-
  // folge (= Farbvergabe). Namen: Beauftragungs-Blasen liefern leadName
  // direkt, Kunden-Blasen nur die ID → Auflösung über die Mitarbeiterliste.
  const { data: allEmployees } = useEmployees();
  const employeeNameById = new Map(
    (allEmployees ?? []).map((e) => [e.id, e.fullName]),
  );
  const leadNameById = new Map<string, string>();
  for (const c of customers) {
    if (c.projectLeadId && !leadNameById.has(c.projectLeadId)) {
      leadNameById.set(
        c.projectLeadId,
        c.leadName ??
          employeeNameById.get(c.projectLeadId) ??
          t("common.dashed"),
      );
    }
  }
  const leadLegend = [...colorMap.entries()].map(([leadId, color]) => ({
    leadId,
    color,
    name: leadNameById.get(leadId) ?? t("common.dashed"),
  }));
  const hasLeadlessBubble = customers.some((c) => !c.projectLeadId);

  const revenues = customers.map((c) => c.revenue);
  const minRev = revenues.length > 0 ? Math.min(...revenues) : 0;
  const maxRev = revenues.length > 0 ? Math.max(...revenues) : 1;

  // Log-Achse kann 0 nicht darstellen (log 0 = −∞) — d3/recharts kollabiert
  // dann die komplette Skala. 0-€-Kunden werden deshalb auf eine halbe
  // Dekade unter dem kleinsten positiven Umsatz gepinnt (linker Rand);
  // Tooltip zeigt weiterhin den echten Wert. Hinweistext unten.
  const positiveRevs = revenues.filter((r) => r > 0);
  const minPositiveRev = positiveRevs.length > 0 ? Math.min(...positiveRevs) : 1;
  const logFloor = minPositiveRev / 3;
  const zeroPinned = logScale && revenues.some((r) => r <= 0);

  // ── Wachstumsachse gegen Ausreißer robust machen (Befund G3) ─────────────
  //
  // Die Skalierung richtet sich nach der Masse der Kunden; wer darüber oder
  // darunter liegt, wird an den Rand gepinnt und dort mit seinem echten Wert
  // beschriftet. Begründung der Grenze: siehe lib/bcgAxis.ts.
  const growthDomain: GrowthDomain = computeGrowthDomain(
    customers.map((c) => c.growth ?? 0),
    thresholds ? [thresholds.growth] : [],
    fullGrowthRange,
  );

  // ── Kundensuche mit Hervorhebung (Befund G3) ─────────────────────────────
  const normalizedQuery = normalizeForSearch(customerQuery.trim());
  const isSearching = normalizedQuery.length > 0;
  const searchMatches = isSearching
    ? customers.filter(
        (c) =>
          normalizeForSearch(c.name).includes(normalizedQuery) ||
          (c.engagementName !== undefined &&
            normalizeForSearch(c.engagementName).includes(normalizedQuery)),
      )
    : [];
  const matchedIds = new Set(searchMatches.map((c) => c.id));

  function canOpenBubble(c: { isMyCustomer: boolean }): boolean {
    return viewerCanOpenAll || c.isMyCustomer;
  }

  // Prepare scatter data
  const scatterData = customers.map((c) => {
    const growth = c.growth ?? 0;
    const { y, pinned } = pinGrowth(growth, growthDomain);
    return {
      id: c.id,
      name: c.name,
      revenue: c.revenue,
      growth,
      quadrant: c.quadrant,
      lastOnsiteVisitDate: c.lastOnsiteVisitDate,
      marker: c.marker,
      customerId: c.customerId,
      engagementName: c.engagementName,
      leadName: c.leadName,
      isMyCustomer: c.isMyCustomer,
      openable: canOpenBubble(c),
      pinned,
      highlighted: isSearching && matchedIds.has(c.id),
      dimmed: isSearching && !matchedIds.has(c.id),
      x: logScale && c.revenue <= 0 ? logFloor : c.revenue,
      y,
      z: bubbleSize(c.revenue, minRev, maxRev),
      fill:
        colorMode === "quadrant"
          ? getQuadrantColor(c.quadrant)
          : getLeadColor(c.projectLeadId, colorMap),
    };
  });

  function handleBubbleClick(data: BcgTooltipPayload) {
    // Beauftragungs-Blasen führen zum Kunden (id wäre die engagementId).
    void navigate({
      to: "/customers/$id",
      params: { id: data.customerId ?? data.id },
    });
  }

  function updateSearch(updates: Partial<BcgSearch>) {
    void navigate({
      to: "/bcg",
      search: (prev: BcgSearch) => ({ ...prev, ...updates }),
    });
  }

  function handleSaveThresholds() {
    if (!resolvedSegmentId) return;
    const rev = typeof revenueOverride === "number" ? revenueOverride : undefined;
    const grw =
      typeof growthOverride === "number" ? growthOverride / 100 : undefined;
    updateThreshold.mutate(
      {
        segmentId: resolvedSegmentId,
        period,
        revenueThreshold: rev ?? null,
        growthThreshold: grw ?? null,
      },
      { onSuccess: () => closeDrawer() },
    );
  }

  function handleResetThresholds() {
    if (!resolvedSegmentId) return;
    updateThreshold.mutate(
      {
        segmentId: resolvedSegmentId,
        period,
        revenueThreshold: null,
        growthThreshold: null,
      },
      { onSuccess: () => closeDrawer() },
    );
  }

  // Quadrant corner labels — placed as reference lines with labels
  const quadrantLabels = thresholds
    ? [
        { value: thresholds.revenue + 1, label: "" }, // dummy — labels done via SVG overlay
      ]
    : [];
  void quadrantLabels; // suppress unused warning — labels are rendered as SVG text

  // ── Render ──────────────────────────────────────────────────────────────────

  if (user && !canSeeBcg) {
    return <Navigate to="/" />;
  }

  if (!resolvedSegmentId) {
    return (
      <Stack gap="md">
        <Title order={2}>{t("pages.bcg.title")}</Title>
        <Alert color="yellow" title={t("pages.bcg.noSegmentTitle")}>
          {t("pages.bcg.noSegmentBody")}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md" h="100%">
      {/* ── Header ── */}
      <Group justify="space-between" align="center">
        <Title order={2}>{t("pages.bcg.title")}</Title>
        {isBereichsleiter && (
          <Button variant="light" size="sm" onClick={openDrawer}>
            {t("pages.bcg.adjustThresholds")}
          </Button>
        )}
      </Group>

      {/* ── Filter Bar ── */}
      <Paper p="sm" withBorder>
        <Group gap="md" wrap="wrap">
          {/* Segment Selector — for GL show all; for others show own segment(s) */}
          {(isBereichsleiter || isGL) && (
            <Select
              label={t("pages.bcg.segmentLabel")}
              value={resolvedSegmentId}
              onChange={(v) => {
                if (v) updateSearch({ segmentId: v });
              }}
              data={
                segmentOptions.length > 0
                  ? segmentOptions
                  : [{ value: resolvedSegmentId, label: resolvedSegmentId }]
              }
              style={{ minWidth: 180 }}
            />
          )}

          {/* Period Kind Tabs */}
          <Stack gap={2}>
            <Text size="xs" fw={500} c="dimmed">
              {t("pages.bcg.periodLabel")}
            </Text>
            <SegmentedControl
              value={periodKind}
              onChange={(v) =>
                updateSearch({
                  periodKind: v as BcgSearch["periodKind"],
                  fiscalYear:
                    v === "fiscal_year"
                      ? (search.fiscalYear ?? String(new Date().getFullYear()))
                      : undefined,
                  periodStart: undefined,
                  periodEnd: undefined,
                })
              }
              data={[
                { value: "rolling_12m", label: t("pages.bcg.periodRollingShort") },
                { value: "fiscal_year", label: t("pages.bcg.periodFiscal") },
                { value: "custom", label: t("pages.bcg.periodCustomShort") },
              ]}
            />
          </Stack>

          {/* Fiscal Year dropdown */}
          {periodKind === "fiscal_year" && (
            <Select
              label={t("pages.bcg.yearLabel")}
              value={search.fiscalYear ?? String(new Date().getFullYear())}
              onChange={(v) => {
                if (v) updateSearch({ fiscalYear: v });
              }}
              data={FISCAL_YEARS}
              style={{ minWidth: 100 }}
            />
          )}

          {/* Custom date range */}
          {periodKind === "custom" && (
            <Group gap="xs" align="flex-end">
              <DatePickerInput
                label={t("pages.bcg.fromLabel")}
                value={search.periodStart ? new Date(search.periodStart) : null}
                onChange={(d) => {
                  if (d) updateSearch({ periodStart: formatIsoDate(d) });
                }}
                valueFormat="DD.MM.YYYY"
                style={{ minWidth: 140 }}
              />
              <DatePickerInput
                label={t("pages.bcg.toLabel")}
                value={search.periodEnd ? new Date(search.periodEnd) : null}
                onChange={(d) => {
                  if (d) updateSearch({ periodEnd: formatIsoDate(d) });
                }}
                valueFormat="DD.MM.YYYY"
                style={{ minWidth: 140 }}
              />
            </Group>
          )}

          {/* Log-Scale Toggle */}
          <Stack gap={2} justify="flex-end">
            <Text size="xs" fw={500} c="dimmed">
              {t("pages.bcg.xAxisLabel")}
            </Text>
            <Switch
              label={t("pages.bcg.logScaleLabel")}
              checked={logScale}
              onChange={(e) => setLogScale(e.currentTarget.checked)}
            />
          </Stack>

          {/* Wachstumsachse: begrenzt vs. volle Spanne (Befund G3) */}
          <Stack gap={2} justify="flex-end">
            <Text size="xs" fw={500} c="dimmed">
              {t("pages.bcg.yAxisLabel")}
            </Text>
            <MantineTooltip
              label={t("pages.bcg.fullGrowthRangeTooltip")}
              multiline
              w={280}
            >
              <Switch
                label={t("pages.bcg.fullGrowthRangeLabel")}
                checked={fullGrowthRange}
                onChange={(e) => setFullGrowthRange(e.currentTarget.checked)}
              />
            </MantineTooltip>
          </Stack>

          {/* Inactive Toggle */}
          <Stack gap={2} justify="flex-end">
            <Text size="xs" fw={500} c="dimmed">
              {t("pages.bcg.displayLabel")}
            </Text>
            <Stack gap={4}>
              <Switch
                label={t("pages.bcg.includeInactiveSwitch")}
                checked={includeInactive}
                onChange={(e) =>
                  updateSearch({ includeInactive: e.currentTarget.checked })
                }
              />
              {/* Befund G1 — reiner Anzeigefilter, für Projektleiter
                  vorbelegt, jederzeit auf den ganzen Bereich umschaltbar. */}
              <MantineTooltip
                label={t("pages.bcg.onlyMineTooltip")}
                multiline
                w={280}
              >
                <Switch
                  label={t("pages.bcg.onlyMineSwitch")}
                  checked={onlyMine}
                  onChange={(e) =>
                    updateSearch({ onlyMine: e.currentTarget.checked })
                  }
                />
              </MantineTooltip>
            </Stack>
          </Stack>

          {/* Färbung: Quadrant vs. Service Lead */}
          <Stack gap={2} justify="flex-end">
            <Text size="xs" fw={500} c="dimmed">
              {t("pages.bcg.colorModeLabel")}
            </Text>
            <SegmentedControl
              size="xs"
              value={colorMode}
              onChange={(v) => setColorMode(v as "quadrant" | "lead")}
              data={[
                { value: "quadrant", label: t("pages.bcg.colorModeQuadrant") },
                { value: "lead", label: t("pages.bcg.colorModeLead") },
              ]}
            />
          </Stack>

          {/* Blase pro Zelle vs. pro Beauftragung (S2-11) */}
          <Stack gap={2} justify="flex-end">
            <Text size="xs" fw={500} c="dimmed">
              {t("pages.bcg.granularityLabel")}
            </Text>
            <SegmentedControl
              size="xs"
              value={granularity}
              onChange={(v) =>
                updateSearch({
                  granularity: v as "customer" | "engagement",
                })
              }
              data={[
                { value: "customer", label: t("pages.bcg.granularityCustomer") },
                { value: "engagement", label: t("pages.bcg.granularityEngagement") },
              ]}
            />
          </Stack>
        </Group>
      </Paper>

      {/* ── Kundensuche mit Hervorhebung (Befund G3) ──
          Die Überlagerung im Zentrum bleibt geometrisch bestehen. Statt zu
          versuchen, sie wegzurechnen, gibt es hier den praktischen Ausweg:
          Kunde tippen, Blase wird hervorgehoben — und der Treffer ist
          zusätzlich als Zeile anklickbar, ohne dass man ihn im Chart treffen
          muss. */}
      <Paper p="sm" withBorder>
        <Stack gap="xs">
          <TextInput
            label={t("pages.bcg.searchLabel")}
            placeholder={t("pages.bcg.searchPlaceholder")}
            value={customerQuery}
            onChange={(e) => setCustomerQuery(e.currentTarget.value)}
            data-testid="bcg-search"
            style={{ maxWidth: 360 }}
          />
          {isSearching && (
            <Stack gap={4} data-testid="bcg-search-results">
              {searchMatches.length === 0 ? (
                <Text size="sm" c="dimmed">
                  {t("pages.bcg.searchNoMatch")}
                </Text>
              ) : (
                <>
                  <Text size="xs" c="dimmed">
                    {t("pages.bcg.searchMatchCount", {
                      count: searchMatches.length,
                    })}
                  </Text>
                  {searchMatches.slice(0, SEARCH_RESULT_LIMIT).map((c) => {
                    const openable = canOpenBubble(c);
                    const label = `${c.name}${
                      c.engagementName ? ` · ${c.engagementName}` : ""
                    } — ${formatRevenue(c.revenue)} / ${formatGrowth(c.growth)}`;
                    return (
                      <Group key={c.id} gap="xs" wrap="nowrap">
                        {openable ? (
                          <Button
                            variant="subtle"
                            size="compact-sm"
                            data-testid="bcg-search-hit"
                            onClick={() =>
                              void navigate({
                                to: "/customers/$id",
                                params: { id: c.customerId ?? c.id },
                              })
                            }
                          >
                            {label}
                          </Button>
                        ) : (
                          <Text
                            size="sm"
                            c="dimmed"
                            data-testid="bcg-search-hit"
                            px={8}
                          >
                            {label}
                          </Text>
                        )}
                      </Group>
                    );
                  })}
                  {searchMatches.length > SEARCH_RESULT_LIMIT && (
                    <Text size="xs" c="dimmed">
                      {t("pages.bcg.searchMore", {
                        count: searchMatches.length - SEARCH_RESULT_LIMIT,
                      })}
                    </Text>
                  )}
                </>
              )}
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* ── Kein Segment auflösbar ── */}
      {!resolvedSegmentId && (
        <Alert color="blue" title={t("pages.bcg.keinSegmentTitel")}>
          {t("pages.bcg.keinSegmentText")}
        </Alert>
      )}

      {/* ── Error ── */}
      {isError && (
        <Alert color="red" title={t("common.error")}>
          {t("pages.bcg.errorBcg")}
        </Alert>
      )}

      {/* ── Chart ── */}
      <Paper
        p="md"
        withBorder
        style={{ flex: 1, minHeight: 500, position: "relative" }}
      >
        {isLoading ? (
          <Stack align="center" justify="center" h={460}>
            <Loader />
            <Text c="dimmed" size="sm">
              {t("pages.bcg.loading")}
            </Text>
          </Stack>
        ) : (
          <>
            {/* Period info — im laufenden Geschäftsjahr steht dazu, dass bis
                heute gegen das Vorjahr bis heute gerechnet wird. Sonst liest
                jemand „2026" und meint das ganze Jahr. */}
            {matrixData?.period.kind === "fiscal_year" &&
              !matrixData.period.current.end.endsWith("-12-31") && (
                <Text size="xs" c="dimmed">
                  {t("pages.bcg.periodFiscalRunningHint")}
                </Text>
              )}
            {matrixData?.period && (
              <Text size="xs" c="dimmed" mb="xs">
                {t("pages.bcg.periodInfo", {
                  currentStart: new Date(matrixData.period.current.start).toLocaleDateString("de-DE"),
                  currentEnd: new Date(matrixData.period.current.end).toLocaleDateString("de-DE"),
                  previousStart: new Date(matrixData.period.previous.start).toLocaleDateString("de-DE"),
                  previousEnd: new Date(matrixData.period.previous.end).toLocaleDateString("de-DE"),
                })}
              </Text>
            )}

            {/* Thresholds info */}
            {thresholds && (
              <Text size="xs" c="dimmed" mb="sm">
                {t("pages.bcg.thresholdsInfo", {
                  revenue: formatRevenue(thresholds.revenue),
                  growth: formatGrowth(thresholds.growth),
                  source:
                    thresholds.source === "manual"
                      ? t("pages.bcg.thresholdsSourceManual")
                      : t("pages.bcg.thresholdsSourceAuto"),
                })}
              </Text>
            )}

            {/* Relativer Wrapper NUR um den Chart: das Quadranten-Overlay
                soll die Info-Zeilen darüber nicht überlappen. */}
            <Box style={{ position: "relative" }}>
            <ResponsiveContainer width="100%" height={460}>
              {/* Größere Margins + Achsen-Padding, damit große Blasen am
                  Rand nicht abgeschnitten werden. */}
              <ScatterChart margin={{ top: 30, right: 60, bottom: 20, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  type="number"
                  dataKey="x"
                  name={t("pages.bcg.tooltipRevenueLabel")}
                  scale={logScale ? "log" : "linear"}
                  domain={logScale ? ["auto", "auto"] : [0, "auto"]}
                  padding={{ left: 40, right: 50 }}
                  tickFormatter={formatRevenue}
                  label={{
                    value: t("pages.bcg.axisRevenueShort"),
                    position: "insideBottom",
                    offset: -10,
                    style: { fontSize: 12 },
                  }}
                />

                <YAxis
                  type="number"
                  dataKey="y"
                  name={t("pages.bcg.tooltipGrowthLabel")}
                  // Feste Domäne statt "auto": Sonst richtete sich die Achse
                  // wieder nach dem Extremwert (Befund G3).
                  domain={[growthDomain.min, growthDomain.max]}
                  allowDataOverflow
                  padding={{ top: 40, bottom: 40 }}
                  tickFormatter={(v: number) => `${(v * 100).toFixed(0)} %`}
                  label={{
                    value: t("pages.bcg.axisGrowth"),
                    angle: -90,
                    position: "insideLeft",
                    style: { fontSize: 12 },
                  }}
                />

                <ZAxis type="number" dataKey="z" range={[10, 3600]} />

                <Tooltip
                  content={<BcgTooltip />}
                  cursor={{ strokeDasharray: "3 3" }}
                />

                {/* 0-line for Y axis */}
                <ReferenceLine
                  y={0}
                  stroke="var(--msc-diagramm-linie)"
                  strokeWidth={1.5}
                  strokeDasharray="4 2"
                />

                {/* Quadrant dividers */}
                {thresholds && (
                  <>
                    <ReferenceLine
                      x={thresholds.revenue}
                      stroke="var(--msc-diagramm-text)"
                      strokeWidth={2}
                      label={{
                        value: formatRevenue(thresholds.revenue),
                        position: "top",
                        style: { fontSize: 11, fill: "var(--msc-diagramm-text)" },
                      }}
                    />
                    <ReferenceLine
                      y={thresholds.growth}
                      stroke="var(--msc-diagramm-text)"
                      strokeWidth={2}
                      label={{
                        value: formatGrowth(thresholds.growth),
                        position: "right",
                        style: { fontSize: 11, fill: "var(--msc-diagramm-text)" },
                      }}
                    />
                  </>
                )}

                <Scatter
                  data={scatterData}
                  shape={(rawProps: unknown) => {
                    const props = rawProps as Record<string, unknown>;
                    const payload = props["payload"] as BcgTooltipPayload | undefined;
                    const fill =
                      (props["fill"] as string | undefined) ??
                      (payload?.fill ?? CHART_COLORS.ist);
                    // payload.z ist unser direkt berechneter Radius aus bubbleSize() —
                    // Recharts' ZAxis-Range würde bei gleichen z-Werten alle auf
                    // ~1-2px schrumpfen lassen. Wir umgehen das.
                    const radius = (payload?.z as number | undefined) ?? 15;
                    return (
                      <BcgDot
                        cx={props["cx"] as number}
                        cy={props["cy"] as number}
                        r={radius}
                        fill={fill}
                        payload={payload}
                        onClick={handleBubbleClick}
                      />
                    );
                  }}
                  fill={CHART_COLORS.ist}
                />
              </ScatterChart>
            </ResponsiveContainer>

            {/* Quadrant corner labels overlay */}
            {thresholds && (
              <QuadrantLabels />
            )}
            </Box>

            {/* Lead-Legende — nur im Service-Lead-Farbmodus (Quadranten
                erklären sich über die Eck-Labels + Blasenfarben selbst). */}
            {colorMode === "lead" && leadLegend.length > 0 && (
              <Group gap="md" mt="xs" wrap="wrap">
                {leadLegend.map((l) => (
                  <Group key={l.leadId} gap={6} wrap="nowrap">
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: l.color,
                        display: "inline-block",
                        flexShrink: 0,
                      }}
                    />
                    <Text size="xs">{l.name}</Text>
                  </Group>
                ))}
                {hasLeadlessBubble && (
                  <Group gap={6} wrap="nowrap">
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: CHART_COLORS.neutral,
                        display: "inline-block",
                        flexShrink: 0,
                      }}
                    />
                    <Text size="xs" c="dimmed">
                      {t("pages.bcg.legendNoLead")}
                    </Text>
                  </Group>
                )}
              </Group>
            )}

            {zeroPinned && (
              <Text size="xs" c="dimmed" mt={4}>
                {t("pages.bcg.logZeroHint")}
              </Text>
            )}

            {/* Begrenzte Achse offenlegen — eine stillschweigend gestauchte
                Skala wäre eine Lüge (Befund G3). */}
            {growthDomain.clamped && (
              <Text size="xs" c="dimmed" mt={4} data-testid="bcg-outlier-hint">
                {t("pages.bcg.growthClampHint", {
                  count: growthDomain.lowOutliers + growthDomain.highOutliers,
                })}{" "}
                {scatterData
                  .filter((d) => d.pinned !== null)
                  .map((d) => `${d.name}: ${formatGrowth(d.growth)}`)
                  .join(" · ")}
              </Text>
            )}

            {/* Anzeigefilter offenlegen (Befund G1): Wer nur seine Kunden
                sieht, soll wissen, dass der Bereich mehr enthält. */}
            {onlyMine && (
              <Text size="xs" c="dimmed" mt={4} data-testid="bcg-only-mine-hint">
                {hiddenByOnlyMine > 0
                  ? t("pages.bcg.onlyMineHint", { count: hiddenByOnlyMine })
                  : t("pages.bcg.onlyMineHintNoneHidden")}
              </Text>
            )}

            {/* Empty state */}
            {customers.length === 0 && !isLoading && (
              <Box
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  pointerEvents: "none",
                }}
              >
                <Text c="dimmed" size="sm">
                  {onlyMine && allBubbles.length > 0
                    ? t("pages.bcg.emptyOnlyMine")
                    : t("pages.bcg.emptyDataset")}
                </Text>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* ── Skeleton placeholder during load ── */}
      {isLoading && (
        <Group>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={20} width={120} />
          ))}
        </Group>
      )}

      {/* ── Threshold Drawer ── */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title={t("pages.bcg.adjustThresholds")}
        position="right"
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t("pages.bcg.drawerThresholdsCurrent")}
          </Text>

          {thresholdsData ? (
            <Stack gap="xs">
              <Text size="sm">
                {t("pages.bcg.drawerRevenueLine", {
                  value: formatRevenue(thresholdsData.revenueThreshold),
                })}
                {thresholdsData.source === "auto" && ` ${t("pages.bcg.thresholdsSourceAuto")}`}
              </Text>
              <Text size="sm">
                {t("pages.bcg.drawerGrowthLine", {
                  value: formatGrowth(thresholdsData.growthThreshold),
                })}
                {thresholdsData.source === "auto" && ` ${t("pages.bcg.thresholdsSourceAuto")}`}
              </Text>
            </Stack>
          ) : (
            <Skeleton height={40} />
          )}

          <NumberInput
            label={t("pages.bcg.drawerRevenueLabel")}
            description={t("pages.bcg.drawerRevenueDescription")}
            placeholder={
              thresholdsData
                ? String(Math.round(thresholdsData.revenueThreshold))
                : t("pages.bcg.drawerRevenuePlaceholderAuto")
            }
            value={revenueOverride}
            onChange={setRevenueOverride}
            min={0}
            step={1000}
            thousandSeparator="."
            decimalSeparator=","
          />

          <NumberInput
            label={t("pages.bcg.drawerGrowthLabel")}
            description={t("pages.bcg.drawerGrowthDescription")}
            placeholder={
              thresholdsData
                ? String(+(thresholdsData.growthThreshold * 100).toFixed(2))
                : t("pages.bcg.drawerRevenuePlaceholderAuto")
            }
            value={growthOverride}
            onChange={setGrowthOverride}
            step={0.5}
            decimalScale={2}
          />

          <Group gap="sm">
            <Button
              onClick={handleSaveThresholds}
              loading={updateThreshold.isPending}
              flex={1}
            >
              {t("pages.bcg.drawerApply")}
            </Button>
            <MantineTooltip label={t("pages.bcg.drawerResetTooltip")}>
              <Button
                variant="subtle"
                color="gray"
                onClick={handleResetThresholds}
                loading={updateThreshold.isPending}
              >
                {t("pages.bcg.drawerResetButton")}
              </Button>
            </MantineTooltip>
          </Group>

          {updateThreshold.isError && (
            <Alert color="red" title={t("common.error")}>
              {t("pages.bcg.drawerSaveError")}
            </Alert>
          )}
        </Stack>
      </Drawer>
    </Stack>
  );
}

// ── Quadrant corner labels (decorative, outside recharts) ─────────────────────

function QuadrantLabels() {
  const { t } = useTranslation();
  return (
    <Box
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        padding: "40px 60px 40px 60px",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "1fr 1fr",
      }}
    >
      {/* Top-left: Question Marks (niedriger Umsatz, hohes Wachstum) */}
      <Text size="xs" fw={700} c="var(--msc-bcg-question-mark)" style={{ alignSelf: "flex-start" }}>
        {t("quadrant.question_mark")}s
      </Text>

      {/* Top-right: Stars (hoher Umsatz, hohes Wachstum) */}
      <Text
        size="xs"
        fw={700}
        c="yellow.7"
        style={{ textAlign: "right", alignSelf: "flex-start" }}
      >
        ★ {t("quadrant.star")}s
      </Text>

      {/* Bottom-left: Dogs (niedriger Umsatz, niedriges Wachstum) */}
      <Text size="xs" fw={700} c="gray.6" style={{ alignSelf: "flex-end" }}>
        {t("quadrant.dog")}s
      </Text>

      {/* Bottom-right: Cash Cows (hoher Umsatz, niedriges Wachstum) */}
      <Text
        size="xs"
        fw={700}
        c="green.7"
        style={{ textAlign: "right", alignSelf: "flex-end" }}
      >
        {t("quadrant.cash_cow")}s
      </Text>
    </Box>
  );
}

// Re-export type so router.ts can import it
export type { BcgCustomer };
